import React, { useEffect, useRef } from "react";
import { useIntl } from "@ai360/core/intl";
import PropTypes from "prop-types";

import { ReportAPI } from "@ai360/core";

import { Checkbox, DateInput, NoLink, Section, SelectInput, SubSection } from "~/core";
import { ReportOptionsComponentProps } from ".";

import { selectors as picklistSelectors, picklistNames, ACTIVE_YN } from "~/core/picklist";
import { getSelectedEventGuidSet } from "~/action-panel/components/event-module/components/event-list/selectors";
import { getEventGeneralGuidToEventSummaryMap } from "~/recs-events/events/selectors";
import { useAppSelector } from "~/store/hooks";
import { messages } from "~/reports/i18n-messages";
import { model } from "~/reports/data";
import CroppingSeason from "~/reports/components/inputs/cropping-season";
import {
    getReportDataByReportType,
    getReportTypePages,
    useReportCropOptions,
} from "~/reports/data/selectors";
import { IS_SELECTABLE } from "~/utils/products/use-product-list";

import { getBasemapOptions } from "../utils";
import { keywords } from "../keywords";
import { ReportPageSelection } from "../components/report-page-selection";

function SkyTissueTestResults({
    hideDefaultOptions,
    onInputChange,
    initialPreferences,
}: ReportOptionsComponentProps) {
    const { formatMessage } = useIntl();
    const [showPageSelection, setShowPageSelection] = React.useState<boolean>(false);

    const reportData = useAppSelector(getReportDataByReportType(SkyTissueTestResults.reportName));
    const selectedEventGuidList = useAppSelector(getSelectedEventGuidSet);
    const agEventSummaryMap = useAppSelector(getEventGeneralGuidToEventSummaryMap);
    const seasons = useAppSelector((state) =>
        picklistSelectors.getPicklistOptionsFromCode(
            state,
            picklistNames.getPickListCode(picklistNames.PICKLIST_CROPPING_SEASON)
        )
    );
    const seasonsToDisplay = useRef<string[]>();
    const disableOption = useRef<boolean>(false);

    const basemapOptions = useAppSelector(getBasemapOptions);
    const crops = useReportCropOptions();

    const disableReportOptionsOnSelectedEvent = (selectedGuidList: Set<string>): boolean => {
        for (const eventGeneralGuid of selectedGuidList) {
            const event = agEventSummaryMap.get(eventGeneralGuid);
            if (event.agEventTypeName === keywords.samplingTissue) {
                return true;
            }
        }
        return false;
    };

    useEffect(() => {
        onInputChange(model.PROPS_SELECTED_EVENT_GUID_LIST, [...selectedEventGuidList]);

        if (hideDefaultOptions) {
            return;
        }

        if (selectedEventGuidList.size > 0) {
            const seasonGuids = [];
            for (const eventGeneralGuid of selectedEventGuidList) {
                const event = agEventSummaryMap.get(eventGeneralGuid);
                if (event.agEventTypeName === keywords.samplingTissue) {
                    seasonGuids.push(event.croppingSeasonGuid);
                }
            }
            const seasonsText = [];
            for (const seasonGuid of seasonGuids) {
                const selectedSeason = seasons.find((season) => season.value === seasonGuid);
                if (seasonsText.indexOf(selectedSeason.label) === -1) {
                    seasonsText.push(selectedSeason.label);
                }
            }
            seasonsText.sort();
            seasonsToDisplay.current = seasonsText;
            disableOption.current = disableReportOptionsOnSelectedEvent(selectedEventGuidList);
        } else {
            seasonsToDisplay.current = [];
            disableOption.current = false;
        }
    }, [selectedEventGuidList, hideDefaultOptions]);

    const croppingSeasonDisabled =
        reportData[model.PROPS_START_DATE] ||
        reportData[model.PROPS_END_DATE] ||
        disableOption.current;
    const selectedCrop =
        crops.find((crop) => crop.value.cropGuid === reportData[model.PROPS_CROP_GUID])?.value ??
        null;

    const attributeSelectionTitle = formatMessage(messages.attributeSelection);

    const reportTypePages = useAppSelector(getReportTypePages);
    const reportPageTypePrefs = initialPreferences[model.PROPS_REPORT_TYPE_PAGE_SELECTION]
        ? JSON.parse(initialPreferences[model.PROPS_REPORT_TYPE_PAGE_SELECTION])
        : {};
    const reportPageSelection = reportData[model.PROPS_REPORT_TYPE_PAGE_SELECTION]
        ? JSON.parse(reportData[model.PROPS_REPORT_TYPE_PAGE_SELECTION])
        : {};

    return (
        <div className="report-options">
            <Section testId="sky-tissue-test-options-section">
                {hideDefaultOptions ? null : (
                    <React.Fragment>
                        <SubSection testId="sky-tissue-test-season-subsection">
                            <CroppingSeason
                                disabled={croppingSeasonDisabled}
                                required={!croppingSeasonDisabled}
                                reportType={SkyTissueTestResults.reportName}
                                valuesToDisplay={seasonsToDisplay.current}
                            />
                            <SelectInput
                                disabled={disableOption.current}
                                optionIsHiddenKey={IS_SELECTABLE}
                                placeholderText={formatMessage(messages.crop)}
                                options={disableOption.current ? [] : crops}
                                onChange={(value) => {
                                    onInputChange(
                                        model.PROPS_CROP_GUID,
                                        value ? value.cropGuid : null
                                    );
                                }}
                                value={disableOption.current ? null : selectedCrop}
                                testId="sky-tissue-test-crop-select"
                            />
                        </SubSection>
                        <SubSection testId="sky-tissue-test-dates-subsection">
                            <DateInput
                                disabled={disableOption.current}
                                placeholderText={formatMessage(messages.startDate)}
                                onChange={(value) => onInputChange(model.PROPS_START_DATE, value)}
                                timeFormat={false}
                                value={reportData[model.PROPS_START_DATE]}
                                testId="sky-tissue-test-start-date-date"
                            />
                            <DateInput
                                disabled={disableOption.current}
                                placeholderText={formatMessage(messages.endDate)}
                                onChange={(value) => onInputChange(model.PROPS_END_DATE, value)}
                                timeFormat={false}
                                value={reportData[model.PROPS_END_DATE]}
                                testId="sky-tissue-test-end-date-date"
                            />
                        </SubSection>
                    </React.Fragment>
                )}
                <SubSection testId="sky-tissue-test-basemap-subsection">
                    <SelectInput
                        required
                        clearable={false}
                        optionIsHiddenKey={ACTIVE_YN}
                        containerClassNames={["display-options-list"]}
                        placeholderText={formatMessage(messages.selectedBasemap)}
                        options={basemapOptions}
                        onChange={(value) => onInputChange(model.PROPS_SELECTED_BASEMAP, value)}
                        value={reportData[model.PROPS_SELECTED_BASEMAP]}
                        testId="sky-tissue-test-basemap-select"
                    />
                    <Checkbox
                        containerClassNames={["custom-imagery"]}
                        onChange={(e, value) =>
                            onInputChange(model.PROPS_USE_CUSTOM_IMAGERY, value)
                        }
                        value={reportData[model.PROPS_USE_CUSTOM_IMAGERY]}
                        label={formatMessage(messages.useCustomImagery)}
                        testId="sky-tissue-test-use-custom-imagery-checkbox"
                    />
                </SubSection>
                <SubSection testId="sky-tissue-test-sample-sites-subsection">
                    <NoLink
                        className="categories-link report-options-modal-link page-selection"
                        label={attributeSelectionTitle}
                        onClick={() => setShowPageSelection(true)}
                    />
                    <ReportPageSelection
                        isOpen={showPageSelection}
                        onChange={(value) => {
                            onInputChange(
                                model.PROPS_REPORT_TYPE_PAGE_SELECTION,
                                JSON.stringify(value)
                            );
                            setShowPageSelection(false);
                        }}
                        onClose={() => setShowPageSelection(false)}
                        title={attributeSelectionTitle}
                        reportTypePages={reportTypePages}
                        reportTypePagesPrefs={reportPageTypePrefs}
                        selectedReportTypes={[SkyTissueTestResults.reportName]}
                        selectedReportTypePages={reportPageSelection}
                    />
                    <Checkbox
                        onChange={(e, value) =>
                            onInputChange(model.PROPS_DISPLAY_SAMPLE_SITES, value)
                        }
                        value={reportData[model.PROPS_DISPLAY_SAMPLE_SITES]}
                        label={formatMessage(messages.displaySampleSites)}
                        testId="sky-tissue-test-display-sample-sites-checkbox"
                    />
                </SubSection>
                <SubSection testId="sky-tissue-test-nutrient-target-colors-subsection">
                    <Checkbox
                        onChange={(e, value) =>
                            onInputChange(model.PROPS_NUTRIENT_TARGET_COLORS, value)
                        }
                        value={reportData[model.PROPS_NUTRIENT_TARGET_COLORS]}
                        label={formatMessage(messages.nutrientTargetColors)}
                        testId="sky-tissue-test-nutrient-target-colors-checkbox"
                    />
                </SubSection>
            </Section>
        </div>
    );
}

SkyTissueTestResults.propTypes = {
    reportPreferences: PropTypes.object,
};

SkyTissueTestResults.defaultReportOptions = {
    [model.PROPS_REPORT_TYPE_PAGE_SELECTION]: null,
    [model.PROPS_SELECTED_EVENT_GUID_LIST]: [],
    [model.PROPS_CROPPING_SEASON_GUID]: null,
    [model.PROPS_CROP_GUID]: null,
    [model.PROPS_START_DATE]: null,
    [model.PROPS_END_DATE]: null,
    [model.PROPS_SELECTED_BASEMAP]: "reportImagery",
    [model.PROPS_DISPLAY_SAMPLE_SITES]: false,
    [model.PROPS_NUTRIENT_TARGET_COLORS]: false,
    [model.PROPS_USE_CUSTOM_IMAGERY]: false,
};

SkyTissueTestResults.reportPreferences = [
    model.PROPS_REPORT_TYPE_PAGE_SELECTION,
    model.PROPS_SELECTED_BASEMAP,
    model.PROPS_DISPLAY_SAMPLE_SITES,
    model.PROPS_NUTRIENT_TARGET_COLORS,
    model.PROPS_USE_CUSTOM_IMAGERY,
];

SkyTissueTestResults.reportName = ReportAPI.ReportNames.AGVANCE_SKY_TISSUE_TEST_RESULTS_REPORT;

export default SkyTissueTestResults;
